import React from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Box,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper
} from '@mui/material';
import { deleteRequest, getRequest, putRequest } from 'Includes/api/apicall';
import { DEL_URL, GET_URL, PUT_URL } from 'Includes/urls';
import { Link } from 'react-router-dom';
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import Swal from 'sweetalert2';

function ServiceList(props) {

  // 🔴 ORIGINAL STATE (UNCHANGED)
  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);

  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editingService, setEditingService] = React.useState(null);
  const [editFormData, setEditFormData] = React.useState({
    name: '',
    description: ''
  });
  const [editErrors, setEditErrors] = React.useState({});
  const [saveLoading, setSaveLoading] = React.useState(false);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  const columns = [
    { field: 'id', headerName: 'ID', width: 90, hide: true },
    { field: 'name', headerName: 'Service Name', flex: 1, minWidth: 200 },
    { field: 'code', headerName: 'Service Code', flex: 1, minWidth: 150 },
    { field: 'description', headerName: 'Description', flex: 2, minWidth: 250 },

    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem label="Edit" onClick={() => handleEdit(params.row)} showInMenu />,
      ]
    }
  ];

  const handleEdit = (row) => {
    setEditingService(row);
    setEditFormData({
      name: row.name || '',
      description: row.description || '',
    });
    setEditErrors({});
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingService(null);
    setEditFormData({ name: '', description: '' });
    setEditErrors({});
  };

  const handleEditFieldChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
    setEditErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleEditSave = () => {
    const errors = {};
    if (!editFormData.name || editFormData.name.trim() === '') {
      errors.name = 'Service Name is required';
    }

    if (Object.keys(errors).length > 0) {
      setEditErrors(errors);
      return;
    }

    setSaveLoading(true);
    const url = `${PUT_URL.services?.api}${editingService.id}/`;
    const data = {
      name: editFormData.name,
      description: editFormData.description,
      code: editingService.code
    };

    putRequest(url, data, props).then(response => {
      setSaveLoading(false);
      if (response && response.status === 200) {
        Swal.fire({
          position: 'top-end',
          type: 'success',
          title: 'Success',
          text: 'Service updated successfully',
          showConfirmButton: false,
          timer: 1500
        });
        handleEditClose();
        getServiceList();
      }
    }).catch(() => setSaveLoading(false));
  };

  const handleDelete = (id) => {
    const url = `${DEL_URL.services?.api}${id}/`;
    setLoading(true);
    deleteRequest(url, {}, props).then(() => {
      getServiceList();
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  React.useEffect(() => {
    getServiceList();
  }, [paginationModel.page, paginationModel.pageSize]);

  const getServiceList = () => {
    setLoading(true);
    const params = {
      limit: paginationModel.pageSize,
      pageno: paginationModel.page + 1,
    };
    getRequest(GET_URL.services?.api, params, props)
      .then(response => {
        if (response && response.status === 200) {
          const data = response.data.data || [];
          const totalCount = response.data?.count || data.length;
          setRows(data);
          setRowCount(totalCount);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  return (
    <Box sx={{ p: 3 }}>

      {/* 🔥 HEADER */}
      <Grid container alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography variant="h5" fontWeight={600}>
            Services
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage available services
          </Typography>
        </Grid>

        <Grid item xs={12} md={6} textAlign="right">
          <Button
            variant="contained"
            component={Link}
            to={'/billing/service'}
            startIcon={<AddCircleOutlineOutlined />}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: 2,
              py: 1,
              boxShadow: "0 4px 14px rgba(0,0,0,0.1)"
            }}
          >
            Add Service
          </Button>
        </Grid>
      </Grid>

      {/* 🔥 TABLE CARD */}
      <Paper
        sx={{
          mt: 3,
          p: 2,
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)"
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          rowCount={rowCount}
          autoHeight
          rowHeight={42}
          disableSelectionOnClick
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f7fb",
              fontWeight: 600,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#fafafa",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #f0f0f0",
            }
          }}
        />
      </Paper>

      {/* 🔥 EDIT DIALOG */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Service</DialogTitle>

        <DialogContent>
          <Box mt={2}>

            <Paper sx={{ p: 2, mb: 2, backgroundColor: "#f5f7fb" }}>
              <Typography variant="caption">Service Code</Typography>
              <Typography variant="h6" fontWeight={600}>
                {editingService?.code || '-'}
              </Typography>
            </Paper>

            <TextField
              label="Service Name"
              fullWidth
              required
              value={editFormData.name}
              onChange={(e) => handleEditFieldChange('name', e.target.value)}
              error={!!editErrors.name}
              helperText={editErrors.name}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Service Description"
              fullWidth
              multiline
              rows={3}
              value={editFormData.description}
              onChange={(e) => handleEditFieldChange('description', e.target.value)}
            />

          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={saveLoading}>
            {saveLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default ServiceList;